import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import UndoOutlinedIcon from "@mui/icons-material/UndoOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { API_URL } from "const/constants";
import { P } from "const/permissions";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useReactToPrint } from "react-to-print";
import { useInvoicePrintTheme } from "hooks/useInvoicePrintTheme";
import { getInvoicePrintPageStyle } from "utils/invoicePrintTheme";
import { hasPermission } from "utils/permissionUtils";
import { formatMoney } from "utils/utils";
import InvoiceTemplate from "../Invoice/InvoiceTemplate";
import SendInvoiceEmailDialog from "components/Invoice/SendInvoiceEmailDialog";
import InvoiceEmailHistoryDialog from "components/Invoice/InvoiceEmailHistoryDialog";
import { getInvoiceGrossAmount, getInvoiceNumber } from "utils/invoiceDashboardUtils";
import { clientInvoicesQueryKey } from "utils/invoiceVerificationQueries";
import ModuleInvoiceDialog from "components/ModuleInvoice/ModuleInvoiceDialog";
import ModuleInvoiceSourceChip from "components/ModuleInvoice/ModuleInvoiceSourceChip";
import { deleteModuleInvoice } from "components/ModuleInvoice/moduleInvoiceApi";
import { isModuleInvoice, moduleLabel } from "components/ModuleInvoice/moduleLinks";
import { canDeleteClientInvoice, canEditClientInvoice } from "./clientInvoiceActions";
import { invalidateModuleFinance } from "components/ModulePayments/moduleFinanceQueries";

const PAYMENT_STATUS_COLORS = {
  PAID: "success",
  PARTIALLY_PAID: "warning",
  UNPAID: "error",
};

/** Activation reviewers identify an invoice by its title, so an untitled draft cannot be sent. */
const hasTitle = (invoice) => Boolean(String(invoice?.title || "").trim());

const ClientInvoices = ({ clientId }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canCreate = hasPermission(P.INVOICE.CLIENT_CREATE);
  const canEmailInvoices = hasPermission(P.INVOICE.SEND);
  const canVerifyInvoices = hasPermission(P.INVOICE.VERIFY);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [revertConfirm, setRevertConfirm] = useState(null);
  const [sendConfirm, setSendConfirm] = useState(null);
  const [previewInvoice, setPreviewInvoice] = useState(null);
  const [emailInvoice, setEmailInvoice] = useState(null);
  const [historyInvoice, setHistoryInvoice] = useState(null);
  const [moduleEdit, setModuleEdit] = useState(null);
  const previewRef = useRef(null);
  const { darkPrint } = useInvoicePrintTheme();
  const printPreview = useReactToPrint({
    contentRef: previewRef,
    pageStyle: getInvoicePrintPageStyle(darkPrint),
    onAfterPrint: () => setPreviewInvoice(null),
  });

  useEffect(() => {
    if (previewInvoice) printPreview();
  }, [previewInvoice]);

  const { data: invoices = [], isFetching } = useQuery({
    queryKey: clientInvoicesQueryKey(clientId),
    queryFn: () =>
      axios
        .get(`${API_URL}/invoice-verification/client/${clientId}`)
        .then((r) => r.data?.data ?? []),
    enabled: Boolean(clientId),
    refetchOnMount: "always",
  });

  const paged = invoices.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const deleteMutation = useMutation({
    mutationFn: (invoice) =>
      isModuleInvoice(invoice)
        ? deleteModuleInvoice(invoice.moduleType, invoice.moduleId)
        : axios.delete(`${API_URL}/invoice-verification/${invoice.invoiceId ?? invoice.id}`),
    onSuccess: (_, invoice) => {
      toast.success("Deleted");
      setDeleteConfirm(null);
      if (isModuleInvoice(invoice)) {
        invalidateModuleFinance(queryClient, invoice.moduleType, invoice.moduleId, clientId);
      }
      queryClient.invalidateQueries(clientInvoicesQueryKey(clientId));
      // Keeps the Invoices tab badge and its unpaid warning in step with the list.
      queryClient.invalidateQueries(["client-summary"]);
      queryClient.invalidateQueries(["invoice"]);
      queryClient.invalidateQueries(["invoiceSummary"]);
      queryClient.invalidateQueries(["outstandingInvoices"]);
    },
    onError: (error) =>
      toast.error(
        error?.response?.data?.errorMessage || error?.response?.data?.message || "Failed to delete"
      ),
  });

  const activationMutation = useMutation({
    mutationFn: (invoiceId) =>
      axios.post(`${API_URL}/invoice-verification/invoices/${invoiceId}/activation/request`),
    onSuccess: () => {
      toast.success("Sent for activation");
      setSendConfirm(null);
      queryClient.invalidateQueries(clientInvoicesQueryKey(clientId));
      // Keeps the Invoices tab badge and its unpaid warning in step with the list.
      queryClient.invalidateQueries(["client-summary"]);
      queryClient.invalidateQueries(["invoice"]);
      queryClient.invalidateQueries(["invoice-activation-pending"]);
      queryClient.invalidateQueries(["invoice-activation-pending-count"]);
    },
    onError: (error) =>
      toast.error(error?.response?.data?.message || "Failed to send for activation"),
  });

  const revertMutation = useMutation({
    mutationFn: (invoiceId) =>
      axios.post(`${API_URL}/invoice-verification/invoices/${invoiceId}/activation/revert`),
    onSuccess: () => {
      toast.success("Reverted to draft");
      setRevertConfirm(null);
      queryClient.invalidateQueries(clientInvoicesQueryKey(clientId));
      // Keeps the Invoices tab badge and its unpaid warning in step with the list.
      queryClient.invalidateQueries(["client-summary"]);
      queryClient.invalidateQueries(["invoice"]);
      queryClient.invalidateQueries(["invoice-activation-pending"]);
      queryClient.invalidateQueries(["invoice-activation-pending-count"]);
    },
    onError: (error) => toast.error(error?.response?.data?.message || "Failed to revert to draft"),
  });

  const handleCreate = () => {
    navigate("/admin/invoice/add", { state: { clientId, mode: "client-verification" } });
  };

  const handleEdit = (invoice) => {
    if (isModuleInvoice(invoice)) {
      setModuleEdit(invoice);
      return;
    }
    const invoiceId = invoice.invoiceId ?? invoice.id;
    navigate("/admin/invoice/add", {
      state: {
        clientId,
        mode: "client-verification",
        verificationId: invoiceId,
        prefill: { ...invoice, invoiceId },
      },
    });
  };

  const canRevertInvoice = (invoice) => {
    if (isModuleInvoice(invoice) || invoice.lifecycleStatus !== "ACTIVE") return false;
    const payments = invoice.payments || [];
    return !payments.some((p) => p.status === "VERIFIED" || p.status === "PENDING_VERIFICATION");
  };

  return (
    <Box className="space-y-6">
      <Box className="flex items-center justify-between">
        <Typography variant="h6">Invoices</Typography>
        {canCreate && (
          <Button variant="contained" startIcon={<AddCircleOutlineIcon />} onClick={handleCreate}>
            Create Invoice
          </Button>
        )}
      </Box>

      <TableContainer className="border border-app-border rounded-lg">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Source</TableCell>
              <TableCell>Lifecycle</TableCell>
              <TableCell>Payment status</TableCell>
              <TableCell>Pending</TableCell>
              <TableCell align="right">Total</TableCell>
              <TableCell align="right">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paged.map((inv) => {
              const invoiceId = inv.invoiceId ?? inv.id;
              const pendingCount = (inv.payments || []).filter(
                (p) => p.status === "PENDING_VERIFICATION"
              ).length;
              const rejectedPayments = (inv.payments || []).filter((p) => p.status === "REJECTED");
              const rejectedCount = rejectedPayments.length;
              return (
                <TableRow key={invoiceId} hover>
                  <TableCell>{invoiceId ?? "-"}</TableCell>
                  <TableCell>{inv.title || "-"}</TableCell>
                  <TableCell>
                    {isModuleInvoice(inv) ? <ModuleInvoiceSourceChip invoice={inv} /> : "-"}
                  </TableCell>
                  <TableCell>
                    {inv.lifecycleStatus === "DRAFT" && inv.activationFeedback ? (
                      <Tooltip title={`Activation rejected: ${inv.activationFeedback}`}>
                        <Chip size="small" label="DRAFT" color="default" />
                      </Tooltip>
                    ) : (
                      <Chip
                        size="small"
                        label={inv.lifecycleStatus || "ACTIVE"}
                        color={
                          inv.lifecycleStatus === "DRAFT"
                            ? "default"
                            : inv.lifecycleStatus === "PENDING_ACTIVATION"
                            ? "warning"
                            : "info"
                        }
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={inv.paymentStatus || "UNPAID"}
                      color={PAYMENT_STATUS_COLORS[inv.paymentStatus] || "default"}
                    />
                  </TableCell>
                  <TableCell>
                    {inv.lifecycleStatus === "PENDING_ACTIVATION" && (
                      <Chip size="small" color="warning" label="activation pending" />
                    )}
                    {pendingCount > 0 && (
                      <Chip size="small" color="warning" label={`${pendingCount} pending`} />
                    )}
                    {rejectedCount > 0 && (
                      <Tooltip
                        componentsProps={{ tooltip: { sx: { whiteSpace: "pre-line" } } }}
                        title={rejectedPayments
                          .map(
                            (p) =>
                              `$${Number(p.amount || 0).toFixed(2)} — ${
                                p.rejectionFeedback || "no reason given"
                              }${p.rejectedByName ? ` (by ${p.rejectedByName})` : ""}`
                          )
                          .join("\n")}
                      >
                        <Chip size="small" color="error" label={`${rejectedCount} rejected`} />
                      </Tooltip>
                    )}
                    {inv.lifecycleStatus !== "PENDING_ACTIVATION" &&
                    pendingCount === 0 &&
                    rejectedCount === 0
                      ? "-"
                      : null}
                  </TableCell>
                  <TableCell align="right">{formatMoney(getInvoiceGrossAmount(inv))}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Preview">
                      <IconButton
                        size="small"
                        onClick={() => setPreviewInvoice(inv)}
                        aria-label="Preview"
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {canEmailInvoices && inv.lifecycleStatus === "ACTIVE" && (
                      <Tooltip title="Email Invoice">
                        <IconButton
                          size="small"
                          onClick={() => setEmailInvoice(inv)}
                          aria-label="Email Invoice"
                        >
                          <EmailOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canEmailInvoices && (
                      <Tooltip title="Email send history">
                        <IconButton
                          size="small"
                          onClick={() => setHistoryInvoice(inv)}
                          aria-label="Email send history"
                        >
                          <HistoryOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canCreate && inv.lifecycleStatus === "DRAFT" && (
                      <Tooltip
                        title={
                          hasTitle(inv)
                            ? "Send for activation"
                            : "Add a title to this invoice before sending it for activation"
                        }
                      >
                        {/* span keeps the tooltip alive while the button is disabled */}
                        <span>
                          <IconButton
                            size="small"
                            disabled={!hasTitle(inv)}
                            onClick={() => setSendConfirm(inv)}
                            aria-label="Send for activation"
                          >
                            <SendOutlinedIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    {canVerifyInvoices && canRevertInvoice(inv) && (
                      <Tooltip title="Revert to draft">
                        <IconButton
                          size="small"
                          onClick={() => setRevertConfirm(inv)}
                          aria-label="Revert to draft"
                        >
                          <UndoOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canEditClientInvoice(inv, canCreate) && (
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleEdit(inv)} aria-label="Edit">
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canDeleteClientInvoice(inv, canCreate) && (
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setDeleteConfirm(inv)}
                          aria-label="Delete"
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
            {!isFetching && invoices.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center" className="text-app-text-muted">
                  No invoices for this client yet.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={invoices.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </TableContainer>

      <InvoiceTemplate printData={previewInvoice || {}} contentRef={previewRef} />

      <SendInvoiceEmailDialog
        open={Boolean(emailInvoice)}
        onClose={() => setEmailInvoice(null)}
        invoiceId={emailInvoice?.invoiceId ?? emailInvoice?.id}
        invoiceNumber={emailInvoice ? getInvoiceNumber(emailInvoice) : ""}
        onSent={() => {
          const sentId = emailInvoice?.invoiceId ?? emailInvoice?.id;
          if (sentId) {
            queryClient.invalidateQueries(["invoiceEmailHistory", sentId]);
          }
          // Client Emails tab reads the same email_send_log by clientId — refresh it too.
          queryClient.invalidateQueries(["client-email-logs"]);
        }}
      />

      <InvoiceEmailHistoryDialog
        open={Boolean(historyInvoice)}
        onClose={() => setHistoryInvoice(null)}
        invoiceId={historyInvoice?.invoiceId ?? historyInvoice?.id}
        invoiceNumber={historyInvoice ? getInvoiceNumber(historyInvoice) : ""}
        clientId={clientId}
      />

      <ModuleInvoiceDialog
        open={Boolean(moduleEdit)}
        mode="edit"
        moduleType={moduleEdit?.moduleType}
        moduleId={moduleEdit?.moduleId}
        clientId={clientId}
        onClose={() => setModuleEdit(null)}
      />

      <Dialog open={Boolean(deleteConfirm)} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>Delete Invoice</DialogTitle>
        <DialogContent>
          <Typography>
            {isModuleInvoice(deleteConfirm)
              ? `Delete this invoice? Its payments stay on the ${moduleLabel(
                  deleteConfirm.moduleType
                )} entry, and a new invoice can be created from there.`
              : `Delete this ${deleteConfirm?.lifecycleStatus === "DRAFT" ? "draft" : "invoice"}${
                  deleteConfirm?.hasPendingPayments
                    ? " and remove pending payments from verification"
                    : ""
                }? This cannot be undone.`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={deleteMutation.isLoading}
            onClick={() => deleteMutation.mutate(deleteConfirm)}
          >
            {deleteMutation.isLoading ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(revertConfirm)} onClose={() => setRevertConfirm(null)}>
        <DialogTitle>Revert to Draft</DialogTitle>
        <DialogContent>
          <Typography>
            Move this invoice back to DRAFT? It will no longer be emailable until it is activated
            again. Only possible because it has no verified or pending payments.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevertConfirm(null)}>Cancel</Button>
          <Button
            color="warning"
            variant="contained"
            disabled={revertMutation.isLoading}
            onClick={() => revertMutation.mutate(revertConfirm.invoiceId ?? revertConfirm.id)}
          >
            {revertMutation.isLoading ? "Reverting…" : "Revert"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(sendConfirm)} onClose={() => setSendConfirm(null)}>
        <DialogTitle>Send for Activation</DialogTitle>
        <DialogContent>
          <Typography>
            Send this draft to the accounts team for activation? It stays non-emailable until
            approved.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendConfirm(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={activationMutation.isLoading}
            onClick={() => activationMutation.mutate(sendConfirm.invoiceId ?? sendConfirm.id)}
          >
            {activationMutation.isLoading ? "Sending…" : "Send"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientInvoices;
